/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { Dispatch, MutableRef, StateUpdater } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  RowActionDetail,
  SortCriterionDetail,
  TableSelectionDetail,
  TableSelectionMode
} from '#UNSAFE_TableView';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ActiveCell,
  ActiveCellDetail,
  ColumnHeaderFooterInfo,
  ScrollDetail,
  SizingInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { useCallback, useRef } from 'preact/hooks';
import {
  getRowActionGestureContext,
  handleRowActionGesture
} from '#PRIVATE_TableCommon/utils/ActionUtils';
import { isLogicalCtrl } from '#PRIVATE_TableCommon/utils/EventUtils';
import {
  getActionContext,
  handleSortActionGesture,
  isSortActionGesture
} from '../utils/TableActionUtils';
import { getElementFromCell, isAutoFocusCell, TABLE_ROW_SELECTOR } from '../utils/TableDomUtils';
import {
  handleActiveColumnHeaderFooterGesture,
  handleEndInteractionColumnHeaderFooterGesture
} from '../utils/TableInteractionUtils';
import {
  handleAdjacentRowGesture,
  handleAdjacentColumnGesture,
  handlePageRowGesture,
  handleJumpColumnGesture
} from '../utils/TableNavigationUtils';
import { handleExtendSelectionGesture, handleSelectionGesture } from '../utils/TableSelectionUtils';

type UseKeyboardHandlingProps<K, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  tableId: string;
  tableData: CollectionData<K, D>[];
  isRtl: boolean;
  isTabbableMode: boolean;
  columnsArray: TableColumn<K, D, C>[];
  sizingInfoRef: MutableRef<SizingInfo>;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  onActiveCellChanged: (
    detail: ActiveCellDetail<K, C>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void;
  activeCell?: ActiveCell<K, C>;
  isDropDownSelection: boolean;
  selectionMode?: TableSelectionMode;
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onTableSelectionChange?: (
    detail: TableSelectionDetail<K, C>,
    isExtendableSelection: boolean
  ) => void;
  activeStyleColumnInfo: ColumnHeaderFooterInfo<C> | undefined;
  setActiveStyleColumnInfo: Dispatch<StateUpdater<ColumnHeaderFooterInfo<C> | undefined>>;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  hideTooltip: (isImmediate?: boolean) => void;
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K, C>) => void;
};

/**
 * Hook that manages keyboard interactions on the Preact Table
 */
export function useKeyboardHandling<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  tableId,
  tableData,
  isRtl,
  isTabbableMode,
  columnsArray,
  sizingInfoRef,
  selectionExtensionElementRef,
  activeCell,
  onActiveCellChanged,
  isDropDownSelection,
  selectionMode,
  selected,
  onTableSelectionChange,
  activeStyleColumnInfo,
  setActiveStyleColumnInfo,
  onRowAction,
  sortCriterion,
  onSortCriterionChange,
  hideTooltip,
  updateDeferredScrollDetail
}: UseKeyboardHandlingProps<K, D, C>) {
  // state for the key that a space or enter keydown occurrs on for matching keyup handling
  const triggerDownKeyRef = useRef<K | undefined>();

  // Keydown handler for the outer Table. This handles all internal
  // keyboard navigation for the Table.
  const _keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        const keyHandlers: Record<string, () => void> = {
          ' ': function () {
            if (activeCell != null) {
              triggerDownKeyRef.current = activeCell.rowKey;
              if (!isTabbableMode) {
                handleActiveColumnHeaderFooterGesture(
                  tableId,
                  event,
                  setActiveStyleColumnInfo,
                  activeStyleColumnInfo,
                  activeCell
                );
                // prevent default action on space to avoid scrolling during a selection
                event.preventDefault();
              }
              if (
                onRowAction != null &&
                getRowActionGestureContext(
                  tableId,
                  tableData,
                  event.target as HTMLElement,
                  TABLE_ROW_SELECTOR,
                  getActionContext,
                  activeCell
                ) != null
              ) {
                event.stopPropagation();
              }
            }
          },
          Enter: function () {
            if (activeCell != null) {
              triggerDownKeyRef.current = activeCell.rowKey;
              if (activeCell.type === 'data') {
                if (isDropDownSelection && !isTabbableMode) {
                  event.preventDefault();
                }
                if (
                  onRowAction != null &&
                  getRowActionGestureContext(
                    tableId,
                    tableData,
                    event.target as HTMLElement,
                    TABLE_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  ) != null
                ) {
                  event.stopPropagation();
                }
              } else if (
                activeCell.type === 'header' &&
                !isTabbableMode &&
                onSortCriterionChange != null &&
                isSortActionGesture(columnsArray, activeCell.columnKey)
              ) {
                event.stopPropagation();
              }
            }
          },
          ArrowUp: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              let isExtendSelectionGesture = false;
              if (
                !isTabbableMode &&
                activeCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                (selectionMode?.row === 'multiple' || selectionMode?.row === 'multipleToggle')
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  rootElement,
                  true,
                  activeCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selected
                );
                isExtendSelectionGesture = true;
              }
              if (
                !isExtendSelectionGesture ||
                isDropDownSelection ||
                selectionMode?.row === 'multipleToggle'
              ) {
                handleAdjacentRowGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  activeCell,
                  onActiveCellChanged,
                  true,
                  isExtendSelectionGesture,
                  hideTooltip,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          ArrowDown: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              let isExtendSelectionGesture = false;
              if (
                !isTabbableMode &&
                activeCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                (selectionMode?.row === 'multiple' || selectionMode?.row === 'multipleToggle')
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  rootElement,
                  false,
                  activeCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selected
                );
                isExtendSelectionGesture = true;
              }
              if (
                !isExtendSelectionGesture ||
                isDropDownSelection ||
                selectionMode?.row === 'multipleToggle'
              ) {
                handleAdjacentRowGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  activeCell,
                  onActiveCellChanged,
                  false,
                  isExtendSelectionGesture,
                  hideTooltip,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          ArrowLeft: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              if (
                !isTabbableMode &&
                (activeCell.type === 'header' || activeCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  rootElement,
                  true,
                  activeCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  activeCell,
                  onActiveCellChanged,
                  !isRtl,
                  hideTooltip,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          ArrowRight: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              if (
                !isTabbableMode &&
                (activeCell.type === 'header' || activeCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  rootElement,
                  false,
                  activeCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(
                  rootElement,
                  tableId,
                  columnsArray,
                  activeCell,
                  onActiveCellChanged,
                  isRtl,
                  hideTooltip,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          PageUp: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              handlePageRowGesture(
                rootElement,
                scrollerElement,
                viewportInfo,
                tableId,
                sizingInfoRef.current,
                activeCell,
                onActiveCellChanged,
                true,
                hideTooltip,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          },
          PageDown: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              handlePageRowGesture(
                rootElement,
                scrollerElement,
                viewportInfo,
                tableId,
                sizingInfoRef.current,
                activeCell,
                onActiveCellChanged,
                false,
                hideTooltip,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          },
          Home: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              handleJumpColumnGesture(
                rootElement,
                tableId,
                columnsArray,
                activeCell,
                onActiveCellChanged,
                true,
                isLogicalCtrl(event),
                hideTooltip,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          },
          End: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, tableId, activeCell))
            ) {
              handleJumpColumnGesture(
                rootElement,
                tableId,
                columnsArray,
                activeCell,
                onActiveCellChanged,
                false,
                isLogicalCtrl(event),
                hideTooltip,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          }
        };
        if (Object.keys(keyHandlers).includes(event.key)) {
          keyHandlers[event.key]();
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      tableId,
      tableData,
      isRtl,
      isTabbableMode,
      columnsArray,
      sizingInfoRef,
      selectionExtensionElementRef,
      activeCell,
      onActiveCellChanged,
      isDropDownSelection,
      selectionMode,
      selected,
      onTableSelectionChange,
      activeStyleColumnInfo,
      setActiveStyleColumnInfo,
      onRowAction,
      onSortCriterionChange,
      hideTooltip,
      updateDeferredScrollDetail
    ]
  );

  // Keyup handler for the outer Table.
  const _keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        const keyHandlers: Record<string, () => void> = {
          ' ': function () {
            if (activeCell != null) {
              handleEndInteractionColumnHeaderFooterGesture(
                setActiveStyleColumnInfo,
                activeStyleColumnInfo
              );
              if (triggerDownKeyRef.current === activeCell.rowKey) {
                if (!isTabbableMode) {
                  if (onTableSelectionChange != null) {
                    handleSelectionGesture(
                      rootElement,
                      tableId,
                      columnsArray,
                      getElementFromCell(rootElement, tableId, activeCell) as HTMLElement,
                      true,
                      onTableSelectionChange,
                      isDropDownSelection,
                      selectionMode,
                      selected
                    );
                  }
                }
                if (
                  onRowAction != null &&
                  handleRowActionGesture(
                    tableId,
                    tableData,
                    onRowAction,
                    event.target as HTMLElement,
                    TABLE_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  )
                ) {
                  event.stopPropagation();
                }
              }
              triggerDownKeyRef.current = undefined;
            }
          },
          Enter: function () {
            if (activeCell != null && triggerDownKeyRef.current === activeCell.rowKey) {
              if (activeCell.type === 'data') {
                if (isDropDownSelection && !isTabbableMode) {
                  if (onTableSelectionChange != null) {
                    handleSelectionGesture(
                      rootElement,
                      tableId,
                      columnsArray,
                      getElementFromCell(rootElement, tableId, activeCell) as HTMLElement,
                      true,
                      onTableSelectionChange,
                      isDropDownSelection,
                      selectionMode,
                      selected
                    );
                  }
                }
                if (
                  onRowAction != null &&
                  handleRowActionGesture(
                    tableId,
                    tableData,
                    onRowAction,
                    event.target as HTMLElement,
                    TABLE_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  )
                ) {
                  event.stopPropagation();
                }
              } else if (
                activeCell.type === 'header' &&
                !isTabbableMode &&
                onSortCriterionChange != null &&
                handleSortActionGesture(
                  columnsArray,
                  activeCell.columnKey,
                  onSortCriterionChange,
                  sortCriterion
                )
              ) {
                event.stopPropagation();
              }
            }
            triggerDownKeyRef.current = undefined;
          }
        };
        if (Object.keys(keyHandlers).includes(event.key)) {
          keyHandlers[event.key]();
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      tableId,
      tableData,
      isTabbableMode,
      columnsArray,
      activeCell,
      activeStyleColumnInfo,
      setActiveStyleColumnInfo,
      isDropDownSelection,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction,
      sortCriterion,
      onSortCriterionChange
    ]
  );

  return { onKeyDown: _keyDownHandler, onKeyUp: _keyUpHandler };
}
